import { useEffect, useState } from 'react';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { COOKIES, PATHS } from '../../config/consts';
import { useCookieConsent } from '../../helpers/cookieConsent';
import actions, { selectors } from '../../modules/cookies';
import CookieConsentComponent from './CookieConsentComponent';
import messages from './messages';

const getSettings = (system: boolean, functional: boolean, marketing: boolean) => [
  {
    checked: system || true,
    cookieName: COOKIES.SYSTEM,
    editable: false,
    info: <FormattedMessage {...messages.cookieConsentSettingsNecessaryInfo} />,
    title: <FormattedMessage {...messages.cookieConsentSettingsNecessaryLabel} />,
  },
  {
    checked: functional || false,
    cookieName: COOKIES.FUNCTIONAL,
    editable: true,
    info: <FormattedMessage {...messages.cookieConsentSettingsFunctionalInfo} />,
    title: <FormattedMessage {...messages.cookieConsentSettingsFunctionalLabel} />,
  },
  {
    checked: marketing || false,
    cookieName: COOKIES.MARKETING,
    editable: true,
    info: <FormattedMessage {...messages.cookieConsentSettingsMarketingInfo} />,
    title: <FormattedMessage {...messages.cookieConsentSettingsMarketingLabel} />,
  },
];

function CookieConsentPanel() {
  const dispatch = useDispatch();
  const isSettingsOpen = useSelector(selectors.getIsCookieSettingsOpen);
  const { cookieConsent, functional, marketing, setCookieConsent, system } = useCookieConsent();
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [screenHeight, setScreenHeight] = useState<number>(0);
  const [settings, setSettings] = useState(getSettings(system, functional, marketing));

  useEffect(() => {
    if (isSettingsOpen) {
      setSettings(getSettings(system, functional, marketing));
    }
  }, [isSettingsOpen]);

  useEffect(() => {
    const handleResize = () => {
      setScreenHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAcceptAll = () => {
    const cookieSettings: Record<string, boolean> = {};
    settings.forEach((item) => {
      cookieSettings[item.cookieName] = true;
    });
    setCookieConsent(cookieSettings);

    dispatch(actions.setCookieSettingsOpen(false));
  };

  const handleSaveSettings = () => {
    const cookieSettings: Record<string, boolean> = {};
    settings.forEach((item) => {
      cookieSettings[item.cookieName] = item.checked;
    });
    setCookieConsent(cookieSettings);

    dispatch(actions.setCookieSettingsOpen(false));
  };

  const handleSettingsChange = (event: React.MouseEvent) => {
    const { name } = event.target as HTMLButtonElement;
    const index = settings.findIndex((item) => item.cookieName === name);
    const settingsArr = [...settings];
    settingsArr[index].checked = !settingsArr[index].checked;

    setSettings(settingsArr);
  };

  return (
    (!cookieConsent || isSettingsOpen) && (
      <CookieConsentComponent
        acceptButtonLabel={<FormattedMessage {...messages.cookieConsentButtonAcceptAll} />}
        fullHeightSettings
        info={<FormattedMessage {...messages.cookieConsentInfo} />}
        links={[
          {
            link: PATHS.COOKIE_POLICY_V2,
            title: <FormattedMessage {...messages.cookieConsentLinkCookies} />,
          },
        ]}
        maxSettingsHeight={screenHeight > 750 ? '360px' : '250px'}
        onAccept={handleAcceptAll}
        onSettingsChange={handleSettingsChange}
        onSettingsClick={
          cookieConsent ? () => dispatch(actions.setCookieSettingsOpen(false)) : () => setSettingsOpen(!settingsOpen)
        }
        onSettingsSave={handleSaveSettings}
        saveSettingsButtonLabel={<FormattedMessage {...messages.cookieConsentButtonSave} />}
        settings={settings}
        settingsButtonLabel={
          cookieConsent ? (
            <FormattedMessage {...messages.cookieConsentButtonClose} />
          ) : (
            <FormattedMessage {...messages.cookieConsentButtonSettings} />
          )
        }
        settingsOpen={cookieConsent ? isSettingsOpen : settingsOpen}
      />
    )
  );
}

export default CookieConsentPanel;
